// @ts-strict-ignore
import { ValidateFilesFn } from '@shared/scenes/upload/hooks';
import {
  BLOCKED_CONTENT_TYPES,
  MBSelectAttachmentType,
  MBUploadUtil,
} from '@shared/scenes/upload/util';
import { API } from '@shared/types/api';
import * as uuid from '@shared/util/uuid';
import { MAX_CHAT_ATTACHMENTS } from './reducer';

function createAttachmentFromGIF(gif: MBGIF): API.ChatAttachmentFragment {
  return {
    url: gif.url,
    name: `${gif.id}.gif`,
    fileKey: uuid.v4(),
    width: gif.width,
    height: gif.height,
    contentType: 'image/gif',
    thumbnails: null,
  };
}

export function enableUrgent(draftId: string) {
  return (dispatch: MBDispatch) => {
    dispatch({ type: 'CHAT:INPUT:UPDATE_URGENT', value: { draftId, urgent: true } });
  };
}
export function disableUrgent(draftId: string) {
  return (dispatch: MBDispatch) => {
    dispatch({ type: 'CHAT:INPUT:UPDATE_URGENT', value: { draftId, urgent: false } });
  };
}

export function updateContent(draftId: string, content: string | undefined) {
  return (dispatch: MBDispatch) =>
    dispatch({ type: 'CHAT:INPUT:UPDATE_CONTENT', value: { draftId, content } });
}

export function resetDraft(draftId: string) {
  return (dispatch: MBDispatch) => {
    dispatch(updateContent(draftId, undefined));
    dispatch(disableUrgent(draftId));
    dispatch(clearAttachments(draftId));
  };
}

export function showGIFModal(draftId: string) {
  return (dispatch: MBDispatch) => {
    dispatch({ type: 'CHAT:INPUT:TOGGLE_GIF_MODAL', value: { draftId, visible: true } });
  };
}
export function hideGIFModal(draftId: string) {
  return (dispatch: MBDispatch) => {
    dispatch({ type: 'CHAT:INPUT:TOGGLE_GIF_MODAL', value: { draftId, visible: false } });
  };
}

export function showTooltipMentionManager() {
  return (dispatch: MBDispatch) => {
    dispatch({ type: 'CHAT:INPUT:TOGGLE_TOOLTIP_MENTION_MANAGER', value: { visible: true } });
  };
}

export function hideTooltips() {
  return (dispatch: MBDispatch) => {
    dispatch({ type: 'CHAT:INPUT:HIDE_TOOLTIPS' });
  };
}

export function selectAttachment(
  type: MBSelectAttachmentType,
  draftId: string,
  validateFiles: ValidateFilesFn,
  onLimitExceeded?: (rejectedCount: number) => void,
) {
  return async (dispatch: MBDispatch, getState: () => MBStoreState) => {
    const attachments = await MBUploadUtil.selectAttachments({ type, multiple: true });
    const validAttachments = validateFiles(attachments).filter(Boolean);
    if (validAttachments.length) {
      const state = getState();
      const currentAttachments = state.chatInput.attachmentsFromDraftId[draftId] || [];
      const availableSlots = MAX_CHAT_ATTACHMENTS - currentAttachments.length;
      const rejectedCount = Math.max(0, validAttachments.length - availableSlots);

      if (rejectedCount > 0) {
        onLimitExceeded?.(rejectedCount);
      }

      dispatch({
        type: 'CHAT:INPUT:ADD_ATTACHMENTS',
        value: { draftId, attachments: validAttachments },
      });
    }
  };
}

export function selectGIF(gif: MBGIF, draftId: string, onLimitExceeded?: () => void) {
  return async (dispatch: MBDispatch, getState: () => MBStoreState) => {
    const state = getState();
    const currentAttachments = state.chatInput.attachmentsFromDraftId[draftId] || [];

    if (currentAttachments.length >= MAX_CHAT_ATTACHMENTS) {
      onLimitExceeded?.();
      dispatch(hideGIFModal(draftId));
      return;
    }

    const attachments = [createAttachmentFromGIF(gif)];
    dispatch({
      type: 'CHAT:INPUT:ADD_ATTACHMENTS',
      value: { draftId, attachments },
    });
    dispatch(hideGIFModal(draftId));
  };
}

export function attachFiles(
  files: File[],
  draftId: string,
  onLimitExceeded?: (rejectedCount: number) => void,
) {
  return async (dispatch: MBDispatch, getState: () => MBStoreState) => {
    const safeFiles = files.filter(f => !BLOCKED_CONTENT_TYPES.includes(f.type));
    const attachments = await Promise.all(safeFiles.map(MBUploadUtil.fileToAttachment));
    const state = getState();
    const currentAttachments = state.chatInput.attachmentsFromDraftId[draftId] || [];
    const availableSlots = MAX_CHAT_ATTACHMENTS - currentAttachments.length;
    const rejectedCount = Math.max(0, attachments.length - availableSlots);

    if (rejectedCount > 0) {
      onLimitExceeded?.(rejectedCount);
    }

    dispatch({
      type: 'CHAT:INPUT:ADD_ATTACHMENTS',
      value: { draftId, attachments },
    });
  };
}

export function removeAttachment(attachment: API.ChatAttachmentFragment, draftId: string) {
  return (dispatch: MBDispatch) => {
    dispatch({
      type: 'CHAT:INPUT:REMOVE_ATTACHMENT',
      value: { draftId, attachment },
    });
  };
}

export function clearAttachments(draftId: string) {
  return (dispatch: MBDispatch) => {
    dispatch({ type: 'CHAT:INPUT:CLEAR_ATTACHMENTS', value: { draftId } });
  };
}
